<div class="cms-dev-settings-container">
  <p-toast position="top-right"></p-toast>
  <p-confirmDialog [style]="{ width: '50vw' }"></p-confirmDialog>

  <div class="page-header">
    <h2>CMS Dev Settings</h2>
    <p class="page-subtitle">Manage live CMS configuration. Changes take effect immediately.</p>
  </div>

  <div *ngIf="isLoading" class="loading-state">
    <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
    <span>Loading settings...</span>
  </div>

  <ng-container *ngIf="!isLoading && cmsSettings">
    <form [formGroup]="settingsForm">
      <p-tabView>
        <!-- CMS Tab -->
        <p-tabPanel header="CMS">
          <div class="settings-section">
            <h3 class="section-title">Automation Flags</h3>
            <div class="settings-grid">
              <div class="setting-row">
                <label>Auto Promote Enabled</label>
                <p-inputSwitch formControlName="autoPromoteEnabled"></p-inputSwitch>
              </div>
              <div class="setting-row">
                <label>Auto Prod Push Enabled</label>
                <p-inputSwitch formControlName="autoProdPushEnabled"></p-inputSwitch>
              </div>
              <div class="setting-row">
                <label>Auto Dev + QA Push Enabled</label>
                <p-inputSwitch formControlName="autoDevQAPushEnabled"></p-inputSwitch>
              </div>
              <div class="setting-row">
                <label>Manual Dev + QA Gamedata Push Enabled</label>
                <p-inputSwitch formControlName="manualDevQAGamedataPushEnabled"></p-inputSwitch>
              </div>
              <div class="setting-row">
                <label>Manual Dev Gamedata Push Enabled</label>
                <p-inputSwitch formControlName="manualDevGamedataPushEnabled"></p-inputSwitch>
              </div>
              <div class="setting-row">
                <label>Manual QA Gamedata Push Enabled</label>
                <p-inputSwitch formControlName="manualQAGamedataPushEnabled"></p-inputSwitch>
              </div>
              <div class="setting-row">
                <label>Manual Prod Push Button Enabled</label>
                <p-inputSwitch formControlName="manualProdPushButtonEnabled"></p-inputSwitch>
              </div>
              <div class="setting-row">
                <label>Sync Resources Button Enabled</label>
                <p-inputSwitch formControlName="syncResourcesButtonEnabled"></p-inputSwitch>
              </div>
              <div class="setting-row">
                <label>AI Color Tagging Enabled</label>
                <p-inputSwitch formControlName="aiColorTaggingEnabled"></p-inputSwitch>
              </div>
              <div class="setting-row">
                <label
                  pTooltip="Model AI Color Tagging runs on. Only models with a known price are listed, so runs can always be estimated and their actual cost recorded."
                  >AI Color Tagger Model</label
                >
                <p-dropdown
                  formControlName="aiColorTaggerModel"
                  [options]="aiColorTaggerModels"
                  placeholder="Unavailable"
                  [style]="{ 'min-width': '10rem' }"
                ></p-dropdown>
              </div>
              <div class="setting-row">
                <label
                  pTooltip="Most a single AI Color Tagging run may cost. Runs estimated above it can't start, and a run stops making AI calls once it reaches it. Empty means no limit. Admins only."
                  >AI Color Tagging Cost Limit / Session</label
                >
                <p-inputNumber
                  formControlName="aiColorTaggerSessionCostLimit"
                  mode="currency"
                  currency="USD"
                  [min]="0"
                  placeholder="No limit"
                  [inputStyle]="{ width: '10rem' }"
                ></p-inputNumber>
              </div>
              <div class="setting-row">
                <label
                  pTooltip="Most AI Color Tagging may cost per calendar month (UTC), across all runs. Empty means no limit. Admins only."
                  >AI Color Tagging Cost Limit / Month</label
                >
                <p-inputNumber
                  formControlName="aiColorTaggerMonthlyCostLimit"
                  mode="currency"
                  currency="USD"
                  [min]="0"
                  placeholder="No limit"
                  [inputStyle]="{ width: '10rem' }"
                ></p-inputNumber>
              </div>
              <small *ngIf="!canEditCostLimits" class="read-only-badge">Cost limits can only be changed by an admin</small>
              <div class="setting-row">
                <label>AI Tagging On Build Enabled</label>
                <p-inputSwitch formControlName="aiTaggingEnabled"></p-inputSwitch>
              </div>
              <div class="setting-row read-only-indicator">
                <label>Gamedata In Progress <span class="read-only-badge">Read Only</span></label>
                <p-inputSwitch formControlName="gamedataInProgress"></p-inputSwitch>
              </div>
            </div>
          </div>

          <div class="settings-section">
            <h3 class="section-title">Worker</h3>
            <div class="settings-grid">
              <div class="setting-row">
                <label>Bull Job Pruning Enabled</label>
                <p-inputSwitch formControlName="bullJobPruningIsEnable"></p-inputSwitch>
              </div>
            </div>
            <p class="section-hint">
              When enabled, one worker instance periodically removes old completed and failed Bull queue jobs from Redis to control memory growth.
            </p>
          </div>
        </p-tabPanel>

        <!-- Asset Builder Tab -->
        <p-tabPanel header="Asset Builder">
          <div class="settings-section">
            <h3 class="section-title">Node Auto-Scaling</h3>
            <div class="settings-grid">
              <div class="setting-row">
                <label>Auto Scale Up Nodes</label>
                <p-inputSwitch formControlName="autoNodesScaleUp"></p-inputSwitch>
              </div>
              <div class="setting-row">
                <label>Auto Scale Down Nodes</label>
                <p-inputSwitch formControlName="autoNodesScaleDown"></p-inputSwitch>
              </div>
            </div>
          </div>

          <div class="settings-section">
            <h3 class="section-title">Build Node Scale Values</h3>
            <div class="settings-grid">
              <div class="setting-row">
                <label>Items Scale Up Value</label>
                <div class="input-with-error">
                  <p-inputNumber formControlName="itemsNodesScaleUpValue" [min]="1" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
                  <small *ngIf="settingsForm.get('itemsNodesScaleUpValue').invalid && settingsForm.get('itemsNodesScaleUpValue').touched" class="p-error">Must be at least 1.</small>
                </div>
              </div>
              <div class="setting-row">
                <label>Items Scale Down Value</label>
                <div class="input-with-error">
                  <p-inputNumber formControlName="itemsNodesScaleDownValue" [min]="1" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
                  <small *ngIf="settingsForm.get('itemsNodesScaleDownValue').invalid && settingsForm.get('itemsNodesScaleDownValue').touched" class="p-error">Must be at least 1.</small>
                </div>
              </div>
              <div class="setting-row">
                <label>Levels Scale Up Value</label>
                <div class="input-with-error">
                  <p-inputNumber formControlName="levelsNodesScaleUpValue" [min]="1" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
                  <small *ngIf="settingsForm.get('levelsNodesScaleUpValue').invalid && settingsForm.get('levelsNodesScaleUpValue').touched" class="p-error">Must be at least 1.</small>
                </div>
              </div>
              <div class="setting-row">
                <label>Levels Scale Down Value</label>
                <div class="input-with-error">
                  <p-inputNumber formControlName="levelsNodesScaleDownValue" [min]="1" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
                  <small *ngIf="settingsForm.get('levelsNodesScaleDownValue').invalid && settingsForm.get('levelsNodesScaleDownValue').touched" class="p-error">Must be at least 1.</small>
                </div>
              </div>
              <div class="setting-row">
                <label>Images Scale Up Value</label>
                <div class="input-with-error">
                  <p-inputNumber formControlName="imagesNodesScaleUpValue" [min]="1" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
                  <small *ngIf="settingsForm.get('imagesNodesScaleUpValue').invalid && settingsForm.get('imagesNodesScaleUpValue').touched" class="p-error">Must be at least 1.</small>
                </div>
              </div>
              <div class="setting-row">
                <label>Images Scale Down Value</label>
                <div class="input-with-error">
                  <p-inputNumber formControlName="imagesNodesScaleDownValue" [min]="1" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
                  <small *ngIf="settingsForm.get('imagesNodesScaleDownValue').invalid && settingsForm.get('imagesNodesScaleDownValue').touched" class="p-error">Must be at least 1.</small>
                </div>
              </div>
            </div>
          </div>

          <div class="settings-section">
            <h3 class="section-title">Build Nodes Auto-Scaling</h3>
            <div class="settings-grid">
              <div class="setting-row">
                <label>Auto-Scaling Enabled</label>
                <p-inputSwitch formControlName="buildNodesAutoScalingEnabled"></p-inputSwitch>
              </div>
              <div class="setting-row">
                <label>Items Max Scale</label>
                <p-inputNumber formControlName="buildNodesAutoScalingItemsMaxScale" [min]="0" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
              </div>
              <div class="setting-row">
                <label>Items Min Scale</label>
                <p-inputNumber formControlName="buildNodesAutoScalingItemsMinScale" [min]="0" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
              </div>
              <div class="setting-row read-only-indicator">
                <label>Items Current Scale <span class="read-only-badge">Read Only</span></label>
                <span class="current-scale-value">{{ buildNodesSettings?.buildNodesAutoScaling?.itemsCurrentScale ?? '—' }}</span>
              </div>
              <div class="setting-row">
                <label>Challenges Max Scale</label>
                <p-inputNumber formControlName="buildNodesAutoScalingChallengesMaxScale" [min]="0" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
              </div>
              <div class="setting-row">
                <label>Challenges Min Scale</label>
                <p-inputNumber formControlName="buildNodesAutoScalingChallengesMinScale" [min]="0" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
              </div>
              <div class="setting-row read-only-indicator">
                <label>Challenges Current Scale <span class="read-only-badge">Read Only</span></label>
                <span class="current-scale-value">{{ buildNodesSettings?.buildNodesAutoScaling?.challengesCurrentScale ?? '—' }}</span>
              </div>
              <div class="setting-row">
                <label>Images Max Scale</label>
                <p-inputNumber formControlName="buildNodesAutoScalingImagesMaxScale" [min]="0" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
              </div>
              <div class="setting-row">
                <label>Images Min Scale</label>
                <p-inputNumber formControlName="buildNodesAutoScalingImagesMinScale" [min]="0" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
              </div>
              <div class="setting-row read-only-indicator">
                <label>Images Current Scale <span class="read-only-badge">Read Only</span></label>
                <span class="current-scale-value">{{ buildNodesSettings?.buildNodesAutoScaling?.imagesCurrentScale ?? '—' }}</span>
              </div>
              <div class="setting-row">
                <label>Minigames Max Scale</label>
                <p-inputNumber formControlName="buildNodesAutoScalingMinigamesMaxScale" [min]="0" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
              </div>
              <div class="setting-row">
                <label>Minigames Min Scale</label>
                <p-inputNumber formControlName="buildNodesAutoScalingMinigamesMinScale" [min]="0" [showButtons]="true" buttonLayout="horizontal" decrementButtonClass="p-button-secondary" incrementButtonClass="p-button-secondary" incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"></p-inputNumber>
              </div>
              <div class="setting-row read-only-indicator">
                <label>Minigames Current Scale <span class="read-only-badge">Read Only</span></label>
                <span class="current-scale-value">{{ buildNodesSettings?.buildNodesAutoScaling?.minigamesCurrentScale ?? '—' }}</span>
              </div>
            </div>
          </div>
        </p-tabPanel>

        <!-- Localization Tab -->
        <p-tabPanel header="Localization">
          <div class="settings-section">
            <h3 class="section-title">Gridly</h3>
            <div class="settings-grid">
              <div class="setting-row">
                <label>Template ID</label>
                <div class="input-with-error">
                  <input pInputText formControlName="gridlyTemplateId" placeholder="Enter template ID" />
                  <small *ngIf="settingsForm.get('gridlyTemplateId').invalid && settingsForm.get('gridlyTemplateId').touched" class="p-error">Only letters, numbers, underscores, and hyphens are allowed.</small>
                </div>
              </div>
            </div>
          </div>

          <div class="settings-section">
            <h3 class="section-title">Processing Queues</h3>
            <div class="settings-grid vertical">
              <div class="setting-row vertical">
                <label>Processing Creation on Gridly</label>
                <p-chips formControlName="processingCreationOnGridly" placeholder="Add entity type and press Enter"></p-chips>
              </div>
              <div class="setting-row vertical">
                <label>Processing Migration on Gridly</label>
                <p-chips formControlName="processingMigrationOnGridly" placeholder="Add entity type and press Enter"></p-chips>
              </div>
              <div class="setting-row vertical">
                <label>Processing Creation for AI</label>
                <p-chips formControlName="processingCreationForAi" placeholder="Add entity type and press Enter"></p-chips>
              </div>
              <div class="setting-row vertical">
                <label>Processing Migration for AI</label>
                <p-chips formControlName="processingMigrationForAi" placeholder="Add entity type and press Enter"></p-chips>
              </div>
            </div>
          </div>
        </p-tabPanel>
      </p-tabView>

      <div class="form-actions" *ngIf="authService.hasPermission(['update'], 'CMSSettingsController')">
        <button
          pButton
          label="Save Settings"
          icon="pi pi-save"
          class="p-button-primary"
          [disabled]="settingsForm.invalid || isSaving"
          (click)="onSaveClick()">
        </button>
        <i *ngIf="isSaving" class="pi pi-spin pi-spinner saving-spinner"></i>
      </div>
    </form>
  </ng-container>

  <div *ngIf="!isLoading && !cmsSettings" class="empty-state">
    <i class="pi pi-exclamation-circle" style="font-size: 2rem"></i>
    <p>No CMS settings document found. Please ensure a settings document with name <code>cms-settings</code> exists in the database.</p>
  </div>
</div>
